import { useEffect, useRef, useState } from "react";
import { ChatInterface } from "./ChatInterface";
import { RuntimeEventsPanel } from "./RuntimeEventsPanel";
import { type RuntimeHealth, describeEnvironmentBanner } from "./runtimeModeViewModel";
import { PayablesTestScreen } from "./PayablesTestScreen";
import { HandoffEntry, LiveChat } from "./LiveChat";
import { wantsHandoff } from "./appHandoff.ts";
import { chooseChatSurface } from "./runtimeModeViewModel";
import { chatConfig } from "./runtimeConfig.ts";

type FixtureSessionResponse = {
  success: boolean;
  session_id: string;
  correlation_id: string;
};

function FixtureChat() {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [turnError, setTurnError] = useState<string | null>(null);
  const conversaRef = useRef<HTMLDivElement>(null);
  // Aberto em tela larga, recolhido em tela estreita: ali o painel empilharia
  // abaixo e empurraria a conversa para fora da primeira dobra (AC #4 e #8).
  //
  // Enquanto o usuário não decide, o painel **acompanha** a viewport. Decidir
  // uma vez na montagem deixava o painel fechado numa tela larga quando a janela
  // nascia estreita e crescia — e aí ele exibe uma contagem de eventos sem
  // mostrar nenhum, que parece defeito.
  const [escolhaDoUsuario, setEscolhaDoUsuario] = useState<boolean | null>(null);
  const [telaLarga, setTelaLarga] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 64rem)").matches,
  );
  useEffect(() => {
    const consulta = window.matchMedia("(min-width: 64rem)");
    const ouvir = (evento: MediaQueryListEvent) => setTelaLarga(evento.matches);
    setTelaLarga(consulta.matches);
    consulta.addEventListener("change", ouvir);
    return () => consulta.removeEventListener("change", ouvir);
  }, []);
  const painelAberto = escolhaDoUsuario ?? telaLarga;
  // Story SF-CHAT-5.3: um turno selecionado, dois lados. Selecionar na conversa
  // destaca os eventos; selecionar no painel destaca a mensagem. O estado mora
  // aqui porque é o pai comum dos dois.
  const [turnoSelecionado, setTurnoSelecionado] = useState<string | null>(null);
  // Story SF-CHAT-6.2: o banner precisa dizer a verdade sobre o modo. Enquanto
  // a saúde não é lida, `null` produz o texto de "modo não confirmado" — nunca
  // o de fixture, que seria presumir simulação num ambiente que pode ser real.
  const [runtime, setRuntime] = useState<RuntimeHealth | null>(null);
  useEffect(() => {
    const ler = async () => {
      try {
        // `/api/config`, não `/health`: o proxy do Vite só encaminha `/api`, e o
        // nginx de produção responde `/health` com um texto fixo que nunca chega
        // ao backend. O conteúdo é o mesmo, da mesma função.
        const resposta = await fetch("/api/config");
        const corpo = await resposta.json();
        setRuntime(corpo?.runtime ?? null);
      } catch {
        setRuntime(null);
      }
    };
    void ler();
  }, []);
  const selecionarTurno = (turno: string | null) => {
    setTurnoSelecionado(turno);
    // Selecionar um turno com o painel fechado não teria efeito visível.
    if (turno && !painelAberto) setEscolhaDoUsuario(true);
  };

  useEffect(() => {
    const launch = async () => {
      try {
        const response = await fetch("/api/internal/fixture-session", { method: "POST" });
        const data = (await response.json()) as Partial<FixtureSessionResponse>;
        if (!response.ok || !data.success || typeof data.session_id !== "string") {
          throw new Error("Não foi possível iniciar o ambiente de teste. Verifique se o backend está no ar e recarregue a página.");
        }
        if (typeof data.correlation_id !== "string") {
          // Sem o segundo fator o turno seria recusado a cada mensagem; falhar
          // aqui, nomeando o problema, em vez de prender a tela carregando.
          throw new Error("O ambiente de teste respondeu sem o identificador de correlação. Recarregue a página; se persistir, o backend está desatualizado.");
        }
        setSessionId(data.session_id);
        setCorrelationId(data.correlation_id);
      } catch (launchError) {
        setError(launchError instanceof Error ? launchError.message : "Não foi possível iniciar o ambiente de teste.");
      }
    };
    void launch();
  }, []);

  const banner = describeEnvironmentBanner(runtime);

  if (error) {
    return (
      <main className="min-h-screen bg-slate-50 p-6 text-slate-900">
        <div role="alert" className="mx-auto max-w-2xl rounded-lg border border-red-300 bg-red-50 p-4">
          <h1 className="text-xl font-semibold text-red-950">Ambiente interno indisponível</h1>
          <p className="mt-2 text-red-900">{error}</p>
          {!banner.unknown && banner.title !== "Ambiente interno de teste." && (
            /* Story SF-CHAT-6.2: quem pediu live via uma falha genérica. O modo
               estava no /health e no log do boot, e não na tela — que é onde a
               pessoa está olhando. Vale para live recusado **e** para live
               ativo: numa falha de arranque, saber que o ambiente aponta para a
               API oficial muda o que se vai investigar. */
            <p className="mt-3 border-t border-red-200 pt-3 text-sm text-red-900">
              <strong>{banner.title}</strong> {banner.body}
            </p>
          )}
        </div>
      </main>
    );
  }

  if (!sessionId || !correlationId) {
    return (
      <main className="min-h-screen bg-slate-50 p-6 text-slate-900">
        <p role="status" aria-live="polite">Preparando ambiente interno de teste…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 p-4">
      <header
        role={banner.live ? "alert" : undefined}
        className={`mx-auto mb-4 max-w-6xl rounded-lg border p-3 text-sm ${
          banner.live
            ? "border-red-400 bg-red-50 text-red-950"
            : banner.unavailable
            ? "border-slate-400 bg-slate-100 text-slate-900"
            : "border-amber-300 bg-amber-50 text-amber-950"
        }`}
      >
        <strong>{banner.title}</strong> {banner.body}
      </header>
      {turnError && (
        <div role="alert" className="mx-auto mb-4 flex max-w-6xl items-start justify-between gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
          <p>{turnError}</p>
          <button
            type="button"
            onClick={() => {
              setTurnError(null);
              // O botão desmonta ao dispensar; sem isto o foco cai no body.
              conversaRef.current?.focus();
            }}
            className="shrink-0 rounded px-2 py-1 font-medium underline underline-offset-2 hover:bg-red-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-700"
          >
            Dispensar
          </button>
        </div>
      )}
      <div
        className={`mx-auto grid max-w-6xl items-start gap-4 ${
          painelAberto ? "lg:grid-cols-[minmax(0,1fr)_22rem]" : "lg:grid-cols-[minmax(0,1fr)_auto]"
        }`}
      >
        <div ref={conversaRef} tabIndex={-1} className="min-w-0">
          <ChatInterface
            sessionId={sessionId}
            correlationId={correlationId}
            onTurnError={setTurnError}
            selectedTurnId={turnoSelecionado}
            onSelectTurn={selecionarTurno}
            allowFileUpload={false}
          />
        </div>
        <RuntimeEventsPanel
          sessionId={sessionId}
          correlationId={correlationId}
          open={painelAberto}
          onToggle={setEscolhaDoUsuario}
          selectedTurnId={turnoSelecionado}
          onSelectTurn={selecionarTurno}
          runtime={runtime}
        />
      </div>
    </main>
  );
}

/**
 * Story SF-CHAT-6.5: a raiz escolhe a superfície pelo modo que o backend
 * **diz** estar vigente. Em live o chat precisa de login no app e de grant por
 * turno; a sessão de fixture nem existe ali (404).
 */
function ChatRoot() {
  const [runtime, setRuntime] = useState<(RuntimeHealth & { fixture_mode?: unknown }) | null | undefined>(undefined);
  useEffect(() => {
    const ler = async () => {
      try {
        const resposta = await fetch("/api/config");
        const corpo = await resposta.json();
        setRuntime(corpo?.runtime ?? null);
      } catch {
        setRuntime(null);
      }
    };
    void ler();
  }, []);
  if (runtime === undefined) {
    return (
      <main className="min-h-screen bg-slate-50 p-6 text-slate-900">
        <p role="status" aria-live="polite">Lendo o modo do ambiente…</p>
      </main>
    );
  }
  if (chooseChatSurface(runtime) === "live") {
    // Story SF-CHAT-9.6: aberto pelo app-v2 — a sessão chega por postMessage.
    if (wantsHandoff(window.location.search, window.opener, window.location.hash)) {
      return <HandoffEntry runtime={runtime} appBaseUrl={chatConfig().VITE_APP_API_BASE_URL} />;
    }
    return <LiveChat runtime={runtime} appBaseUrl={chatConfig().VITE_APP_API_BASE_URL} />;
  }
  return <FixtureChat />;
}

function App() {
  if (window.location.pathname === "/contas-a-pagar") {
    return <PayablesTestScreen />;
  }
  if (window.location.pathname === "/") return <ChatRoot />;
  return <main className="min-h-screen bg-slate-50 p-6 text-slate-900"><h1 className="text-xl font-semibold">Página não encontrada</h1></main>;
}

export default App;
